import type { UpdateSettings } from "@reviewer73/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { toast } from "sonner";
import { api } from "../api/client.js";
import { InheritedModelField } from "../components/inherited-model-field.js";
import { InheritedNumberField } from "../components/inherited-number-field.js";
import { formatCost, formatDuration } from "../lib/format.js";
import { EmptyNote } from "../components/empty-note.js";
import { Rack } from "../components/rack.js";
import { StaggerBody, StaggerRow } from "../components/stagger.js";
import { Table, TableCell, TableEmpty, TableHead, TableHeader, TableRow } from "../components/ui/table.js";

/** What an empty field falls back to — named in words, in the row's label column. */
const ENV_SOURCE = "den Wert aus der Umgebung des Deployments";
const PROJECT_MODEL_SOURCE = "das Modell des jeweiligen Projekts";

/**
 * The global defaults every project inherits. Each field can be cleared, which
 * hands it back to the deployment's environment variable — shown as the
 * placeholder, so the effective value is never hidden.
 */
export function Settings() {
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ["settings"], queryFn: api.settings });
  const stats = useQuery({ queryKey: ["stats", 30], queryFn: () => api.stats(30) });

  const update = useMutation({
    mutationFn: (body: UpdateSettings) => api.updateSettings(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["settings"] });
      // Project pages show these values as inherited placeholders.
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
    onError: (err) =>
      toast.error("Änderung fehlgeschlagen", { description: (err as Error).message }),
  });

  if (settings.isLoading) {
    return <EmptyNote>Einstellungen werden geladen…</EmptyNote>;
  }
  if (!settings.data) {
    return (
      <div className="tile">
        <EmptyNote>Einstellungen nicht erreichbar.</EmptyNote>
      </div>
    );
  }

  const { stored, fallback } = settings.data;
  const effectiveMaxDiff = stored.defaultMaxDiffLines ?? fallback.defaultMaxDiffLines;
  const effectiveFanOut = stored.fanOutAboveLines ?? fallback.fanOutAboveLines;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold tracking-tight">Einstellungen</h1>
        <p className="mt-1 max-w-prose text-sm text-muted-foreground">
          Standardwerte für alle Projekte. Ein leeres Feld erbt den grauen Wert; jedes Projekt kann
          jeden Wert überschreiben.
        </p>
      </div>

      <Rack label="Standardwerte">
        <Rows>
          <Row
            label="Diff-Limit"
            hint="Größere MRs werden übersprungen."
            inherits={stored.defaultMaxDiffLines === null ? ENV_SOURCE : undefined}
          >
            <InheritedNumberField
              aria-label="Diff-Limit"
              value={stored.defaultMaxDiffLines}
              inherited={fallback.defaultMaxDiffLines}
              inheritedLabel="den Wert aus der Umgebung"
              min={1}
              onChange={(defaultMaxDiffLines) => update.mutate({ defaultMaxDiffLines })}
            />
          </Row>

          <Row
            label="Agent-Timeout"
            hint="Sekunden je Lauf. Fan-out bekommt anteilig mehr."
            inherits={stored.agentTimeoutSeconds === null ? ENV_SOURCE : undefined}
          >
            <InheritedNumberField
              aria-label="Agent-Timeout in Sekunden"
              value={stored.agentTimeoutSeconds}
              inherited={fallback.agentTimeoutSeconds}
              inheritedLabel="den Wert aus der Umgebung"
              min={1}
              onChange={(agentTimeoutSeconds) => update.mutate({ agentTimeoutSeconds })}
            />
          </Row>

          <Row
            label="Standard-Modell"
            hint="Gilt für neu entdeckte Projekte."
            inherits={stored.defaultModel === null ? ENV_SOURCE : undefined}
          >
            <InheritedModelField
              aria-label="Standard-Modell"
              value={stored.defaultModel}
              inheritedLabel="den Wert aus der Umgebung"
              onChange={(defaultModel) => update.mutate({ defaultModel })}
            />
          </Row>
        </Rows>
      </Rack>

      <Rack label="Fan-out">
        <Rows
          lead={
            effectiveFanOut >= effectiveMaxDiff
              ? `Fan-out ist aus: die Schwelle (${effectiveFanOut}) liegt nicht unter dem Diff-Limit (${effectiveMaxDiff}).`
              : `Bis ${effectiveFanOut} Zeilen prüft ein Agent, bis ${effectiveMaxDiff} Zeilen teilt ein Lead-Agent den Diff auf Subagents auf, darüber wird übersprungen.`
          }
        >
          <Row
            label="Fan-out ab"
            hint="0 heisst: immer Fan-out."
            inherits={stored.fanOutAboveLines === null ? ENV_SOURCE : undefined}
          >
            <InheritedNumberField
              aria-label="Fan-out ab"
              value={stored.fanOutAboveLines}
              inherited={fallback.fanOutAboveLines}
              inheritedLabel="den Wert aus der Umgebung"
              onChange={(fanOutAboveLines) => update.mutate({ fanOutAboveLines })}
            />
          </Row>

          <Row
            label="Max. Subagents"
            hint="Obergrenze für den Lead-Agent."
            inherits={stored.maxSubagents === null ? ENV_SOURCE : undefined}
          >
            <InheritedNumberField
              aria-label="Maximale Subagents"
              value={stored.maxSubagents}
              inherited={fallback.maxSubagents}
              inheritedLabel="den Wert aus der Umgebung"
              min={1}
              onChange={(maxSubagents) => update.mutate({ maxSubagents })}
            />
          </Row>

          <Row
            label="Subagent-Modell"
            inherits={stored.subagentModel === null ? PROJECT_MODEL_SOURCE : undefined}
          >
            <InheritedModelField
              aria-label="Subagent-Modell"
              value={stored.subagentModel}
              inheritedLabel="das Modell des Projekts"
              onChange={(subagentModel) => update.mutate({ subagentModel })}
            />
          </Row>
        </Rows>
      </Rack>

      <Rack label="Architektur & Ansatz">
        <Rows lead="Ein zweiter Durchgang beurteilt den MR als Ganzes statt zeilenweise — nur beim ersten Review, nur im Summary, nie inline.">
          <Row
            label="Ab Zeilen"
            hint="0 heisst: jeder MR."
            inherits={stored.architectureMinLines === null ? ENV_SOURCE : undefined}
          >
            <InheritedNumberField
              aria-label="Architektur-Schritt ab Zeilen"
              value={stored.architectureMinLines}
              inherited={fallback.architectureMinLines}
              inheritedLabel="den Wert aus der Umgebung"
              onChange={(architectureMinLines) => update.mutate({ architectureMinLines })}
            />
          </Row>

          <Row
            label="Architektur-Modell"
            hint="Kleiner Prompt, kurze Antwort — hier lohnt ein stärkeres Modell."
            inherits={stored.architectureModel === null ? PROJECT_MODEL_SOURCE : undefined}
          >
            <InheritedModelField
              aria-label="Architektur-Modell"
              value={stored.architectureModel}
              inheritedLabel="das Modell des Projekts"
              onChange={(architectureModel) => update.mutate({ architectureModel })}
            />
          </Row>
        </Rows>
      </Rack>

      <Rack label="Modelle" hint="Kosten und Geschwindigkeit der letzten 30 Tage">
        <div className="tile p-1">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Modell</TableHead>
                <TableHead className="text-right">Reviews</TableHead>
                <TableHead className="text-right">Kosten</TableHead>
                <TableHead className="text-right">Ø Kosten</TableHead>
                <TableHead className="text-right">Ø Dauer</TableHead>
                <TableHead className="text-right">Ø Tokens/s</TableHead>
              </TableRow>
            </TableHeader>
            {!stats.data || stats.data.models.length === 0 ? (
              <TableEmpty colSpan={6}>
                {stats.isLoading
                  ? "Wird geladen…"
                  : "Noch keine abgeschlossenen Reviews in den letzten 30 Tagen."}
              </TableEmpty>
            ) : (
              <StaggerBody>
                {stats.data.models.map((m) => (
                  <StaggerRow key={m.model}>
                    <TableCell className="font-mono">{m.model}</TableCell>
                    <TableCell className="text-right tabular-nums">{m.reviews}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCost(m.costUsd)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCost(m.avgCostUsd)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatDuration(m.avgDurationS)}
                    </TableCell>
                    <TableCell
                      className="text-right tabular-nums"
                      title={
                        m.avgTokensPerSec === null
                          ? "Der Agent hat für dieses Modell keine Token-Zahlen gemeldet."
                          : undefined
                      }
                    >
                      {m.avgTokensPerSec === null ? "—" : m.avgTokensPerSec.toFixed(1)}
                    </TableCell>
                  </StaggerRow>
                ))}
              </StaggerBody>
            )}
          </Table>
          <p className="max-w-prose px-2 pb-2 pt-3 text-xs text-muted-foreground">
            Tokens/s ist der Durchsatz über den ganzen Lauf, inklusive Tool-Aufrufen — gut zum
            Vergleich der Modelle, kein Rohwert des Providers.
          </p>
        </div>
      </Rack>
    </div>
  );
}

/**
 * One tile of settings: an optional sentence that states what the group does
 * with the values that are actually set, then hairline-separated rows.
 */
function Rows({ lead, children }: { lead?: string; children: ReactNode }) {
  return (
    <div className="tile px-5 py-1">
      {lead !== undefined ? (
        <p className="max-w-prose border-b border-border py-3 text-sm text-muted-foreground">
          {lead}
        </p>
      ) : null}
      <dl className="divide-y divide-border">{children}</dl>
    </div>
  );
}

/**
 * Name and consequence on the left, the control on the right — one rhythm for
 * every setting. Where an empty field falls back to is said here, under the
 * label, and not as a word dangling off the input: that word made every row end
 * at a different place, which is what a column of controls must never do.
 */
function Row({
  label,
  hint,
  inherits,
  children,
}: {
  label: string;
  hint?: string;
  /** Shown while the field is empty, e.g. "aus der Umgebung". */
  inherits?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 py-3">
      <dt className="min-w-40 max-w-prose flex-1">
        <span className="text-sm font-semibold">{label}</span>
        {hint !== undefined ? (
          <span className="block text-xs text-muted-foreground">{hint}</span>
        ) : null}
        {inherits !== undefined ? (
          <span className="block text-xs text-muted-foreground">Leer: erbt {inherits}.</span>
        ) : null}
      </dt>
      <dd className="flex items-center justify-end">{children}</dd>
    </div>
  );
}
